import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faFilter } from "@fortawesome/free-solid-svg-icons";
import { Star } from "lucide-react";
import ProductCard from "../../Frontend/shop/ProdCard";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../ui/Button";
import { useLocation } from "react-router-dom";
import { Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent } from "@chakra-ui/react";
import Shoploader from "../loader/Shoploader";
import { getShadeNumberList } from "../../../redux/reduxApi/admin/ShadeReduxApi";
import { getUniqueShade } from "../../../redux/api/admin/shadeApi";
import { getTags } from "../../../redux/reduxApi/admin/TagReduxApi";
import { preloadImages } from "./utils";

// Memoized ProductCard to prevent unnecessary re-renders
const MemoizedProductCard = React.memo(ProductCard, (prevProps, nextProps) => {
  return (
    prevProps.product?.ROWID === nextProps.product?.ROWID &&
    prevProps.index === nextProps.index
  );
});

const Shop = () => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCategory = queryParams.get("category") || "All";
  const shadeNameFromURL = queryParams.get("shadeName") || "";
  const searchFromURL = queryParams.get("search") || "";

  const [searchTerm, setSearchTerm] = useState(shadeNameFromURL);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const finalSearch = debouncedSearchTerm || searchFromURL;
  const pageSize = 30;

  // Optimized debounce with cleanup
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (shadeNameFromURL) {
      setSearchTerm(shadeNameFromURL);
    }
  }, [shadeNameFromURL]);

  const [category, setCategory] = useState(initialCategory);
  const [brand, setBrand] = useState("All");
  const [loading, setLoading] = useState(true);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [rating, setRating] = useState("All");

  const [tempMinPrice, setTempMinPrice] = useState(minPrice);
  const [tempMaxPrice, setTempMaxPrice] = useState(maxPrice);
  const [tempRating, setTempRating] = useState(rating);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [productCount, setProductCount] = useState(0);
  const dispatch = useDispatch();

  const getShadeNumberData = useSelector((state) => state?.shade?.shade);
  const getTagsData = useSelector((state) => state?.tags?.tags);

  const [productList, setProductList] = useState([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // Tags fetching — does NOT control loading state to avoid race conditions
  useEffect(() => {
    const fetchTags = async () => {
      try {
        await dispatch(getTags());
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.error("Error fetching tags data:", error);
        }
      }
    };

    fetchTags();
  }, [dispatch]);

  // Optimized scroll effect
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.search]);

  // Optimized search products effect
  useEffect(() => {
    if (!finalSearch.trim()) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);

        const body = {
          page: 1,
          value: finalSearch,
        };

        let response = await getUniqueShade(body);
        let result = response?.data;
        let data = [];

        if (result?.source === "products") {
          data = result?.datafirst || [];
        } else if (result?.source === "shade") {
          data = result?.data || [];
        }

        setProductList(data);

        // Preload first 4 product images for faster perceived performance
        const imagesToPreload = data
          .slice(0, 4)
          .map((item) => {
            let media = item.shadeMedia;
            if (typeof media === "string") {
              try {
                media = JSON.parse(media);
              } catch {
                media = [];
              }
            }
            if (Array.isArray(media) && media.length > 0) {
              return media[0];
            }
            if (
              item.items &&
              Array.isArray(item.items) &&
              item.items.length > 0 &&
              item.items[0]?.itemImages
            ) {
              try {
                const parsed = JSON.parse(item.items[0].itemImages);
                return parsed[0]?.filePath;
              } catch {
                return null;
              }
            }
            return null;
          })
          .filter(Boolean);

        preloadImages(imagesToPreload).catch(() => {
          // Silently fail preloading — images will load normally
        });
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.error("Search error:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [finalSearch, dispatch]);

  // Optimized main products fetch
  useEffect(() => {
    if (finalSearch) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        if (searchTerm) {
          const body = {
            page: currentPage,
            size: pageSize,
            search: [
              {
                field: "shadeName",
                operation: "is",
                value: searchTerm,
              },
            ],
          };
          await dispatch(getShadeNumberList(body));
        } else {
          await dispatch(
            getShadeNumberList({ page: currentPage, size: pageSize })
          );
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.error("Error fetching Product data:", error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch, currentPage, pageSize, finalSearch, searchTerm]);

  // Optimized product list update from Redux + preload images
  useEffect(() => {
    if (getShadeNumberData && getShadeNumberData?.length > 0) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("getShadeNumberData", getShadeNumberData);
      }
      setProductList(getShadeNumberData);

      if (getShadeNumberData.length === pageSize) {
        setTotalPages(currentPage + 1);
      }

      // Preload first 4 product images for faster perceived performance
      const imagesToPreload = getShadeNumberData
        .slice(0, 4)
        .map((item) => {
          let media = item.shadeMedia;
          if (typeof media === "string") {
            try {
              media = JSON.parse(media);
            } catch {
              media = [];
            }
          }
          if (Array.isArray(media) && media.length > 0) {
            return media[0];
          }
          if (
            item.items &&
            Array.isArray(item.items) &&
            item.items.length > 0 &&
            item.items[0]?.itemImages
          ) {
            try {
              const parsed = JSON.parse(item.items[0].itemImages);
              return parsed[0]?.filePath;
            } catch {
              return null;
            }
          }
          return null;
        })
        .filter(Boolean);

      preloadImages(imagesToPreload).catch(() => {
        // Silently fail preloading
      });
    }
  }, [getShadeNumberData, pageSize, currentPage]);

  // Optimized total pages calculation
  useEffect(() => {
    setTotalPages(Math.ceil(productCount / pageSize));
  }, [productCount, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, category, brand]);

  // Memoized page change handler
  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [totalPages]);

  // Memoized rating change handler
  const handleRatingChange = useCallback((value) => {
    setRating(value);
  }, []);

  // Memoized price handlers
  const handleMinPriceChange = useCallback((e) => {
    const value = Number(e.target.value);
    if (value <= maxPrice) {
      setMinPrice(value);
    }
  }, [maxPrice]);

  const handleMaxPriceChange = useCallback((e) => {
    const value = Number(e.target.value);
    if (value >= minPrice) {
      setMaxPrice(value);
    }
  }, [minPrice]);

  // Memoized reset filters handler
  const handleResetFilters = useCallback(() => {
    setTempMinPrice(0);
    setTempMaxPrice(1000);
    setTempRating("All");
    setSearchTerm("");
    setCategory("All");
    setBrand("All");

    setMinPrice(0);
    setMaxPrice(1000);
    setRating("All");
    setCurrentPage(1);
  }, []);

  // Memoized pagination renderer
  const renderPagination = useCallback(() => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-10">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-full font-medium transition-colors duration-200
    ${currentPage === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-[#66944b] hover:bg-[#FFFFFF] text-[#ffffff] hover:text-[#66944b] border-2 border-[#effbf5] hover:border-[#66944b]"}`}
            title={currentPage === 1 ? "You're on the first page" : "Go to previous page"}
          >
            &larr; Prev
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else {
              const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
              pageNum = startPage + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`w-10 h-10 rounded-full font-medium transition-colors duration-200
    ${currentPage === pageNum ? "bg-[#011638] text-[#dff7eb]" : "bg-[#dff7eb] text-[#011638] hover:bg-[#011638] hover:text-[#dff7eb] border border-[#011638]"}`}
                title={`Go to page ${pageNum}`}
              >
                {pageNum}
              </button>
            );
          })}

          {totalPages > 5 && currentPage < totalPages - 2 && <span className="px-2">...</span>}

          {totalPages > 5 && currentPage < totalPages - 1 && (
            <button
              onClick={() => handlePageChange(totalPages)}
              className={`w-10 h-10 rounded-full font-medium transition-colors duration-200
    ${currentPage === totalPages ? "bg-[#011638] text-[#dff7eb]" : "bg-[#dff7eb] text-[#011638] hover:bg-[#011638] hover:text-[#dff7eb] border border-[#011638]"}`}
              title="Go to last page"
            >
              {totalPages}
            </button>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-full font-medium transition-colors duration-200
    ${currentPage === totalPages ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-[#66944b] hover:bg-[#FFFFFF] text-[#ffffff] hover:text-[#66944b] border-2 border-[#effbf5] hover:border-[#66944b]"
              }`}
            title={currentPage === totalPages ? "You're on the last page" : "Go to next page"}
          >
            Next &rarr;
          </button>
        </div>
      </div>
    );
  }, [totalPages, currentPage, handlePageChange]);

  // Memoized rating filter renderer
  const renderRatingFilter = useCallback(() => {
    const ratings = [5, 4, 3, 2, 1];

    return (
      <div className="flex flex-col space-y-3">
        {ratings.map((rate) => (
          <label key={rate} className="flex items-center cursor-pointer text-[#011638]">
            <input
              type="checkbox"
              className="mr-3 w-5 h-5 rounded border-gray-300 focus:ring-2 focus:ring-amber-400"
              checked={tempRating === rate.toString()}
              onChange={() => setTempRating(tempRating === rate.toString() ? "All" : rate.toString())}
            />
            <div className="flex items-center space-x-1">
              {Array.from({ length: 5 }, (_, i) => (
                <Star key={i} size={18} className={i < rate ? "text-yellow-400" : "text-gray-300 visible-invisible"} />
              ))}
              <span className="ml-3 text-[#011638] font-semibold">{rate}</span>
            </div>
          </label>
        ))}
      </div>
    );
  }, [tempRating]);

  // Memoized price filter renderer
  const renderPriceFilter = useCallback(() => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-[#011638] mb-2">Price ($)</label>
        <div className="flex items-center space-x-2 mb-2">
          <input
            type="number"
            min={0}
            max={tempMaxPrice}
            value={tempMinPrice}
            onChange={(e) => setTempMinPrice(Number(e.target.value))}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-[#011638]"
          />
          <span className="text-[#011638]">-</span>
          <input
            type="number"
            min={tempMinPrice}
            max={1000}
            value={tempMaxPrice}
            onChange={(e) => setTempMaxPrice(Number(e.target.value))}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-[#011638]"
          />
        </div>
      </div>
    );
  }, [tempMinPrice, tempMaxPrice]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fbfbfb] to-[#f7f7f7]">
      {loading ? (
        <div className="relative w-full h-[90vh]">
          <Shoploader />
        </div>
      ) : (
        <div className="sm:px-8">
          {/* Mobile Filter Icon - Hidden on laptop */}
          <div className="lg:hidden">
            <div className="h-3 md:h-4"></div>
            <div className="px-4 mb-0 lg:mb-4">
              <button
                className="flex items-center gap-2 text-[#011638] font-semibold"
                onClick={() => setIsMobileFilterOpen(true)}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Filter</span>
              </button>
            </div>
          </div>

          {/* Main Layout */}
          <div className="max-w-auto flex flex-col lg:flex-row gap-3.5 px-2.5 sm:px-5">
            {/* Desktop Sidebar - Sticky on laptop */}
            <aside className="w-full lg:w-80 h-auto lg:h-screen bg-[#f3f3f3] text-[#011638] border overflow-auto border-gray-300 shadow-md rounded p-4 lg:sticky lg:top-28 lg:self-start hidden lg:block">
              <h2 className="text-xl font-bold mb-4 flex items-center text-[#661e33]">
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Filter Products
              </h2>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#011638] mb-2">Search</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1 w-full border border-gray-300 text-black px-3 py-2 rounded-md pr-10 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#dff7eb] focus:border-[#dff7eb] transition duration-200"
                  />
                  <FontAwesomeIcon icon={faMagnifyingGlass} rotation={90} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="flex gap-2 justify-between mt-6">
                <Button
                  text="Reset"
                  className="bg-[#66944b] hover:bg-[#FFFFFF] text-[#ffffff] hover:!text-[#66944b] border-2 border-[#effbf5] hover:border-[#66944b] px-4 py-2 rounded-full flex gap-2 items-center text-lg sm:text-xl lg:text-lg font-bold"
                  onClick={handleResetFilters}
                />
              </div>
            </aside>

            {/* Product Grid */}
            <main className="flex-1 mt-1 lg:!mt-8">
              <div>
                {/* Fixed grid for equal card widths on all devices */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 w-full mx-auto px-1 sm:px-0">
                  {productList?.map((shade, idx) => (
                    <div key={shade.ROWID} className="w-full min-w-0">
                      <MemoizedProductCard product={shade} index={idx} />
                    </div>
                  ))}
                  {productList?.length === 0 && (
                    <p className="text-gray-500 col-span-full text-center mt-10">
                      No products found.
                    </p>
                  )}
                </div>
              </div>
              {renderPagination()}
            </main>
          </div>

          {/* Mobile Drawer using Chakra UI */}
          <Drawer isOpen={isMobileFilterOpen} placement="left" onClose={() => setIsMobileFilterOpen(false)}>
            <DrawerOverlay />
            <DrawerContent color="#011638">
              <DrawerHeader borderBottomWidth="1px" className="text-lg font-extrabold">
                <FontAwesomeIcon icon={faFilter} />
                &nbsp;Filter Products
              </DrawerHeader>
              <DrawerBody>
                <div className="space-y-4 mt-2 md:mt-4">
                  <div>
                    <label className="text-sm font-semibold">Search</label>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full mt-1 border border-gray-300 text-black px-3 py-2 rounded-md"
                    />
                  </div>

                  <Button
                    text="Reset"
                    className="bg-[#66944b] hover:bg-[#FFFFFF] text-white hover:!text-[#66944b] border-2 border-[#effbf5] hover:border-[#66944b] px-4 py-2 rounded-full flex gap-2 items-center font-bold mt-4 transition-colors duration-200"
                    onClick={handleResetFilters}
                  />
                </div>
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </div>
      )}
    </div>
  );
};

export default Shop;

